import React, { useState, useEffect } from 'react'
import { PageHeader } from '@/components/layout'
import { useCourses } from '@/hooks/useCourses'
import { useEnrollmentsByCourse } from '@/hooks/useEnrollments'
import { useAuth } from '@/hooks/useAuth'
import {
  useGradingScheme,
  useCreateGradingScheme,
  useSetGradingScheme,
  usePracticalScoresBatch,
} from '@/hooks/useGrading'
import { PracticalScoreRow } from '@/components/grading/PracticalScoreRow'
import type { GradingComponent } from '@/types/grading'
import type { Enrollment } from '@/types/enrollment'

type ComponentType = GradingComponent['type']

const COMPONENT_TYPES: { value: ComponentType; label: string }[] = [
  { value: 'quiz', label: 'Quiz' },
  { value: 'practical', label: 'Practical' },
  { value: 'attendance', label: 'Attendance' },
  { value: 'assignment', label: 'Assignment' },
]

const DEFAULT_COMPONENTS: GradingComponent[] = [
  { type: 'quiz', weightPercentage: 50, passingThreshold: 70 },
  { type: 'practical', weightPercentage: 50, passingThreshold: 70 },
]

export const AdminGradingPage: React.FC = () => {
  const { user, hasPermission } = useAuth()
  const [selectedCourseId, setSelectedCourseId] = useState('')
  const [components, setComponents] = useState<GradingComponent[]>(DEFAULT_COMPONENTS)
  const [submitError, setSubmitError] = useState('')
  const [submitSuccess, setSubmitSuccess] = useState('')

  const canManageScheme = hasPermission('grading.manage_scheme')

  const { data: coursesData, isLoading: loadingCourses } = useCourses()
  const { data: schemeData, isLoading: loadingScheme } = useGradingScheme(selectedCourseId)
  const createScheme = useCreateGradingScheme()
  const updateScheme = useSetGradingScheme()
  const { data: enrollmentsData, isLoading: loadingEnrollments } = useEnrollmentsByCourse(selectedCourseId)

  const courses = Array.isArray(coursesData) ? coursesData : (coursesData as any)?.data || []
  const enrollments: Enrollment[] = Array.isArray(enrollmentsData)
    ? enrollmentsData
    : (enrollmentsData as any)?.data || []

  const enrollmentIds = enrollments.map((e) => e.id)
  const { data: batchScoresData } = usePracticalScoresBatch(enrollmentIds)
  const batchScores: Record<string, any[]> = batchScoresData || {}

  useEffect(() => {
    if (schemeData?.components) {
      setComponents(schemeData.components)
    } else if (selectedCourseId) {
      setComponents(DEFAULT_COMPONENTS)
    }
    setSubmitError('')
    setSubmitSuccess('')
  }, [schemeData, selectedCourseId])

  const totalWeight = components.reduce((sum, c) => sum + (c.weightPercentage || 0), 0)

  const handleWeightChange = (index: number, value: number) => {
    setComponents((prev) =>
      prev.map((c, i) => (i === index ? { ...c, weightPercentage: value } : c))
    )
    setSubmitError('')
    setSubmitSuccess('')
  }

  const handleThresholdChange = (index: number, value: number) => {
    setComponents((prev) =>
      prev.map((c, i) => (i === index ? { ...c, passingThreshold: value } : c))
    )
  }

  const handleTypeChange = (index: number, type: ComponentType) => {
    setComponents((prev) => prev.map((c, i) => (i === index ? { ...c, type } : c)))
  }

  const handleAddComponent = () => {
    setComponents((prev) => [...prev, { type: 'quiz', weightPercentage: 0, passingThreshold: 70 }])
  }

  const handleRemoveComponent = (index: number) => {
    setComponents((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError('')
    setSubmitSuccess('')

    if (!selectedCourseId) {
      setSubmitError('Please select a course.')
      return
    }

    if (totalWeight !== 100) {
      setSubmitError(`Total weight must equal 100%. Currently: ${totalWeight}%`)
      return
    }

    if (components.length === 0) {
      setSubmitError('At least one grading component is required.')
      return
    }

    try {
      if (schemeData) {
        await updateScheme.mutateAsync({ courseId: selectedCourseId, components })
        setSubmitSuccess('Grading scheme updated successfully.')
      } else {
        await createScheme.mutateAsync({ courseId: selectedCourseId, components })
        setSubmitSuccess('Grading scheme created successfully.')
      }
    } catch (err: any) {
      setSubmitError(err?.message || 'Failed to save grading scheme.')
    }
  }

  const isPending = createScheme.isPending || updateScheme.isPending

  return (
    <div>
      <PageHeader
        title="Grading"
        subtitle="Configure grading scheme and record practical scores"
      />

      {!canManageScheme && (
        <div className="text-center py-12">
          <div className="size-20 rounded-full bg-white dark:bg-surface-card mx-auto mb-4 flex items-center justify-center">
            <span className="material-symbols-outlined text-[32px] text-slate-400">lock</span>
          </div>
          <p className="text-gray-900 dark:text-white font-bold mb-2">Akses Ditolak</p>
          <p className="text-sm text-slate-500">Anda tidak memiliki akses untuk mengelola skema penilaian. Hubungi Super Admin untuk mendapatkan akses.</p>
        </div>
      )}

      {canManageScheme && (
      <>
      {/* Course Selector */}
      <div className="mb-6 max-w-md">
        <label className="block text-sm font-medium text-gray-700 dark:text-text-muted mb-2">
          Select Course
        </label>
        {loadingCourses ? (
          <div className="h-10 bg-gray-100 dark:bg-surface-card rounded-xl animate-pulse" />
        ) : (
          <select
            value={selectedCourseId}
            onChange={(e) => setSelectedCourseId(e.target.value)}
            className="w-full px-4 py-2.5 bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white focus:outline-none focus:border-primary/50 transition-colors"
          >
            <option value="">-- Choose a course --</option>
            {courses.map((course: any) => (
              <option key={course.id} value={course.id}>
                {course.title}
              </option>
            ))}
          </select>
        )}
      </div>

      {selectedCourseId && (
        <>
          {/* Grading Scheme Section */}
          <form onSubmit={handleSubmit}>
            <div className="rounded-2xl bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-bold text-gray-900 dark:text-white">
                  {loadingScheme ? 'Loading scheme...' : schemeData ? 'Edit Grading Scheme' : 'New Grading Scheme'}
                </h2>
                <span
                  className={`text-sm font-bold px-3 py-1 rounded-full ${
                    totalWeight === 100
                      ? 'bg-emerald-500 text-white'
                      : 'bg-rose-500 text-white'
                  }`}
                >
                  Total: {totalWeight}%
                </span>
              </div>

              <div className="space-y-4">
                {components.map((component, index) => (
                  <div
                    key={index}
                    className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl bg-gray-50 dark:bg-surface-dark border border-gray-100 dark:border-white/5"
                  >
                    <div className="flex-1 min-w-0">
                      <label className="block text-xs text-gray-500 dark:text-text-muted mb-1">Type</label>
                      <select
                        value={component.type}
                        onChange={(e) => handleTypeChange(index, e.target.value as ComponentType)}
                        className="w-full px-3 py-2 bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:border-primary/50"
                      >
                        {COMPONENT_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="w-32">
                      <label className="block text-xs text-gray-500 dark:text-text-muted mb-1">Weight (%)</label>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={component.weightPercentage}
                        onChange={(e) => handleWeightChange(index, Number(e.target.value))}
                        className="w-full px-3 py-2 bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:border-primary/50"
                      />
                    </div>
                    <div className="w-36">
                      <label className="block text-xs text-gray-500 dark:text-text-muted mb-1">Pass Threshold (%)</label>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={component.passingThreshold}
                        onChange={(e) => handleThresholdChange(index, Number(e.target.value))}
                        className="w-full px-3 py-2 bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:border-primary/50"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveComponent(index)}
                      className="self-end sm:self-auto size-9 rounded-lg hover:bg-red-500/10 flex items-center justify-center transition-colors text-red-400"
                      title="Remove component"
                    >
                      <span className="material-symbols-outlined text-[18px]">delete</span>
                    </button>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={handleAddComponent}
                className="mt-4 flex items-center gap-2 text-sm text-primary hover:text-primary/80 font-medium transition-colors"
              >
                <span className="material-symbols-outlined text-[18px]">add_circle</span>
                Add Component
              </button>
            </div>

            {submitError && (
              <div className="mb-4 px-4 py-3 rounded-xl bg-red-500/10 text-red-600 dark:text-red-400 text-sm">
                {submitError}
              </div>
            )}
            {submitSuccess && (
              <div className="mb-4 px-4 py-3 rounded-xl bg-green-500/10 text-green-600 dark:text-green-400 text-sm">
                {submitSuccess}
              </div>
            )}

            <button
              type="submit"
              disabled={isPending || totalWeight !== 100}
              className="flex items-center gap-2 px-6 py-2.5 bg-primary text-white rounded-xl font-bold text-sm hover:scale-105 transition-transform shadow-lg shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 mb-8"
            >
              {isPending ? (
                <>
                  <div className="size-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined text-[18px]">save</span>
                  {schemeData ? 'Update Scheme' : 'Create Scheme'}
                </>
              )}
            </button>
          </form>

          {/* Practical Score Section */}
          <div className="border-t border-gray-200 dark:border-white/10 pt-8">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Practical Scores</h2>
            <p className="text-sm text-gray-500 dark:text-text-muted mb-6">
              Record practical scores for enrolled students in this course.
            </p>

            {loadingEnrollments ? (
              <div className="text-center py-12 text-gray-500 dark:text-text-muted">
                Loading enrollments...
              </div>
            ) : enrollments.length === 0 ? (
              <div className="text-center py-16 text-gray-500 dark:text-text-muted">
                <div className="size-20 rounded-full bg-white dark:bg-surface-card mx-auto mb-4 flex items-center justify-center">
                  <span className="material-symbols-outlined text-[32px]">people</span>
                </div>
                <p className="text-gray-900 dark:text-white font-bold mb-2">No enrollments</p>
                <p className="text-sm">No students enrolled in this course yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {enrollments.map((enrollment) => (
                  <PracticalScoreRow
                    key={enrollment.id}
                    enrollment={enrollment}
                    trainerId={user?.id || ''}
                    scores={batchScores[enrollment.id] || []}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {!selectedCourseId && (
        <div className="text-center py-16 text-gray-500 dark:text-text-muted">
          <div className="size-20 rounded-full bg-white dark:bg-surface-card mx-auto mb-4 flex items-center justify-center">
            <span className="material-symbols-outlined text-[32px]">grading</span>
          </div>
          <p className="text-gray-900 dark:text-white font-bold mb-2">Select a course</p>
          <p className="text-sm">Choose a course above to configure grading and record practical scores.</p>
        </div>
      )}
      </>
      )}
    </div>
  )
}

export default AdminGradingPage
